import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { TopBar } from '../../components/TopBar/TopBar';
import { OrganizationDetailViewModel } from '../../viewModels/OrganizationDetailViewModel';
import './OrganizationDetailView.css';

interface Props {
  viewModel: OrganizationDetailViewModel;
  onBack: () => void;
}

const placeholder = '/placeholder.svg';

export const OrganizationDetailView: React.FC<Props> = observer(({ viewModel, onBack }) => {
  const { organization, loading, error } = viewModel;
  const [currentCertIndex, setCurrentCertIndex] = useState(0);

  // 获取证书列表
  const certificates = React.useMemo(() => {
    if (!organization) return [];
    const certs: string[] = [];
    if (organization.registrationCert) certs.push(organization.registrationCert);
    if (organization.fundraisingCert) certs.push(organization.fundraisingCert);
    if (organization.socialRatingCert) certs.push(organization.socialRatingCert);
    return certs;
  }, [organization]);

  return (
    <div className="org-detail-page">
      <TopBar title="机构信息" showBack onBack={onBack} />

      {loading && <div className="org-detail-loading">加载中...</div>}
      {error && <div className="org-detail-error">{error}</div>}

      {!loading && organization && (
        <div className="org-detail-content">
          {/* 机构 Logo 和名称 */}
          <div className="org-detail-header">
            <div className="org-detail-logo">
              {organization.logoUrl ? (
                <img 
                  src={organization.logoUrl} 
                  alt={organization.name}
                  onError={(e) => {
                    e.currentTarget.src = placeholder;
                  }}
                />
              ) : (
                <div className="org-detail-logo-placeholder">🏢</div>
              )}
            </div>
            <div className="org-detail-name">{organization.name}</div>
          </div>

          {/* 机构介绍 */}
          {organization.introduction && (
            <div className="org-detail-section">
              <div className="org-detail-section-title">机构介绍</div>
              <div className="org-detail-intro">{organization.introduction}</div>
            </div>
          )}

          {/* 机构公众号 */}
          <div className="org-detail-section">
            <div className="org-detail-section-title">机构公众号</div>
            {organization.wechatQrCode ? (
              <div className="org-detail-qrcode">
                <img 
                  src={organization.wechatQrCode} 
                  alt="微信公众号二维码"
                  onError={(e) => {
                    e.currentTarget.src = placeholder;
                  }}
                />
                <div className="org-detail-qrcode-desc">
                  扫码关注微信公众号 随时获得项目和进展的消息
                </div>
              </div>
            ) : (
              <div className="org-detail-placeholder">
                <img src={placeholder} alt="二维码占位" />
                <div className="org-detail-qrcode-desc">
                  扫码关注微信公众号 随时获得项目和进展的消息
                </div>
              </div>
            )}
          </div>

          {/* 机构证书 */}
          {certificates.length > 0 && (
            <div className="org-detail-section">
              <div className="org-detail-section-title">机构证书</div>
              <div className="org-detail-certificates">
                <div className="org-detail-cert-main">
                  <img 
                    src={certificates[currentCertIndex]} 
                    alt={`证书 ${currentCertIndex + 1}`}
                    onError={(e) => {
                      e.currentTarget.src = placeholder;
                    }}
                  />
                </div>
                {certificates.length > 1 && (
                  <div className="org-detail-cert-nav">
                    <button 
                      className="org-detail-cert-nav-btn"
                      disabled={currentCertIndex === 0}
                      onClick={() => setCurrentCertIndex(Math.max(0, currentCertIndex - 1))}
                    >
                      ‹
                    </button>
                    <span className="org-detail-cert-indicator">
                      {currentCertIndex + 1} / {certificates.length}
                    </span>
                    <button 
                      className="org-detail-cert-nav-btn"
                      disabled={currentCertIndex === certificates.length - 1}
                      onClick={() => setCurrentCertIndex(Math.min(certificates.length - 1, currentCertIndex + 1))}
                    >
                      ›
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});



